/**
 * Admin-only pricingExplanation tree. Never mount on customer/PWA surfaces.
 */

import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { buildPricingExplanationTree } from "../services/pricingExplanationTree.js";

const Tree = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(1, 1.25),
  maxHeight: 420,
  overflow: "auto",
}));

const NodeRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: theme.spacing(1),
  minHeight: 22,
}));

const QuotePricingExplanationNode = ({ node, depth = 0 }) => {
  const children = Array.isArray(node.children) ? node.children : [];
  const hasChildren = children.length > 0;

  return (
    <Box
      sx={{ pl: depth === 0 ? 0 : 1.5 }}
      data-testid={`pricing-explanation-node-${node.key}`}
    >
      <NodeRow>
        <Typography
          variant={depth === 0 ? "caption" : "body2"}
          sx={{
            fontWeight: depth === 0 ? 700 : 500,
            color: depth === 0 ? "text.secondary" : "text.primary",
          }}
        >
          {node.label}
        </Typography>
        {node.value != null && (
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, textAlign: "right", flexShrink: 0 }}
          >
            {node.value}
          </Typography>
        )}
      </NodeRow>
      {hasChildren && (
        <Stack spacing={0.25} mt={0.25}>
          {children.map((child) => (
            <QuotePricingExplanationNode
              key={child.key}
              node={child}
              depth={depth + 1}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const QuotePricingExplanationTree = ({
  explanation = null,
  title = "Pricing explanation (internal)",
}) => {
  const nodes = buildPricingExplanationTree(explanation);

  return (
    <Box data-testid="quote-pricing-explanation-tree" mb={1.5}>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {title}
      </Typography>
      {nodes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No pricing explanation on this draft yet.
        </Typography>
      ) : (
        <Tree>
          <Stack spacing={1}>
            {nodes.map((node) => (
              <QuotePricingExplanationNode key={node.key} node={node} />
            ))}
          </Stack>
        </Tree>
      )}
    </Box>
  );
};

export default QuotePricingExplanationTree;
